import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkle, CircleNotch, TrendUp, TrendDown, ArrowUp, ArrowDown, Minus, Lightning, WarningCircle, Star,
} from "@phosphor-icons/react";

function Delta({ value, suffix = "", invert = false }) {
  if (value == null) return <span className="text-zinc-300">—</span>;
  const up = value > 0;
  const good = invert ? value < 0 : value > 0;
  const color = value === 0 ? "#a1a1aa" : good ? "#16A34A" : "#EF4444";
  const Icon = value === 0 ? Minus : up ? ArrowUp : ArrowDown;
  return (
    <span className="inline-flex items-center gap-0.5 text-xs font-bold" style={{ color }}>
      <Icon size={12} weight="bold" />{value > 0 ? "+" : ""}{value}{suffix}
    </span>
  );
}

function MetricCard({ label, current, prev, delta, suffix = "", star = false }) {
  return (
    <div className="bg-white/10 rounded-xl p-3.5">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-white/60">{label}</div>
      <div className="mt-1 font-display font-extrabold text-2xl flex items-center gap-1">
        {current != null ? current : "—"}{star && current != null && <Star size={15} weight="fill" className="text-amber-300" />}{suffix}
      </div>
      <div className="mt-0.5"><Delta value={delta} suffix={suffix} /></div>
    </div>
  );
}

export default function WeeklyInsights() {
  const [trends, setTrends] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [gen, setGen] = useState(false);

  const load = useCallback(() => {
    api.get("/insights/trends").then((r) => setTrends(r.data)).catch(() => setTrends(false));
    api.get("/insights/weekly").then((r) => setWeekly(r.data)).catch(() => setWeekly(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGen(true);
    try {
      const { data } = await api.post("/insights/weekly");
      if (data.empty) toast.info("Kör AI-analys på Kvalitet-sidan först så finns data att sammanfatta.");
      else { setWeekly(data); toast.success("Veckorapport genererad"); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte generera rapporten");
    } finally { setGen(false); }
  };

  if (!trends || trends === false || !trends.has_data) return null;
  const m = trends.metrics || {};

  return (
    <div className="mt-6 rounded-2xl border border-[#0033FF]/20 bg-gradient-to-br from-[#0B1437] to-[#1E2A6B] text-white p-6 lg:p-7 shadow-lg" data-testid="weekly-insights">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-display font-bold text-xl tracking-tight flex items-center gap-2">
          <Sparkle size={22} weight="fill" className="text-amber-300" /> Veckans insikter
        </h2>
        <button data-testid="generate-weekly-button" onClick={generate} disabled={gen}
          className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 rounded-full px-4 py-2 font-semibold text-sm disabled:opacity-50 transition-colors">
          {gen ? <CircleNotch size={15} className="animate-spin" /> : <Sparkle size={15} weight="fill" />}
          {weekly?.summary ? "Uppdatera rapport" : "Generera AI-rapport"}
        </button>
      </div>

      <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="weekly-metrics">
        <MetricCard label="Snittkvalitet" current={m.quality?.current} delta={m.quality?.delta} />
        <MetricCard label="Lösningsgrad" current={m.resolved_rate?.current} delta={m.resolved_rate?.delta} suffix="%" />
        <MetricCard label="CSAT" current={m.csat?.current} delta={m.csat?.delta} star />
        <MetricCard label="Volym (7 dgr)" current={m.volume?.current} delta={m.volume?.delta} />
      </div>

      {(trends.rising_topics?.length > 0 || trends.falling_topics?.length > 0) && (
        <div className="mt-5 flex flex-wrap gap-2" data-testid="trend-alerts">
          {trends.rising_topics.map((t) => (
            <span key={`r-${t.topic}`} data-testid="trend-rising" className="inline-flex items-center gap-1.5 text-sm font-semibold bg-amber-400/20 text-amber-200 rounded-full px-3 py-1.5">
              <TrendUp size={15} weight="bold" /> {t.topic} <span className="text-amber-300">+{t.change_pct}%</span>
            </span>
          ))}
          {trends.falling_topics.map((t) => (
            <span key={`f-${t.topic}`} data-testid="trend-falling" className="inline-flex items-center gap-1.5 text-sm font-semibold bg-white/10 text-white/70 rounded-full px-3 py-1.5">
              <TrendDown size={15} weight="bold" /> {t.topic} <span className="text-white/50">{t.change_pct}%</span>
            </span>
          ))}
        </div>
      )}

      <div className="mt-5 rounded-xl bg-white/5 border border-white/10 p-5">
        {weekly?.summary ? (
          <>
            {weekly.highlights?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {weekly.highlights.map((h, i) => (
                  <span key={i} className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1 ${h.type === "warning" ? "bg-red-400/20 text-red-200" : h.type === "positive" ? "bg-emerald-400/20 text-emerald-200" : "bg-amber-400/20 text-amber-200"}`}>
                    {h.type === "warning" ? <WarningCircle size={12} weight="fill" /> : <Lightning size={12} weight="fill" />} {h.text}
                  </span>
                ))}
              </div>
            )}
            <p className="text-sm text-white/85 leading-relaxed whitespace-pre-wrap" data-testid="weekly-summary-text">{weekly.summary}</p>
            {weekly.generated_at && (
              <p className="text-[11px] text-white/40 mt-3">Genererad {(weekly.generated_at || "").slice(0, 16).replace("T", " ")} · vecka {weekly.week}</p>
            )}
          </>
        ) : (
          <div className="text-center py-4" data-testid="weekly-empty">
            <p className="text-sm text-white/70">Få en AI-skriven sammanfattning av veckan — kvalitet, topp-ämnen, sentiment och rekommendationer.</p>
            <p className="text-xs text-white/40 mt-1">Klicka "Generera AI-rapport".</p>
          </div>
        )}
      </div>
    </div>
  );
}
